import { claimDemoJob } from "../services/demoEntryAnalysisApi";
import type { DemoJobPollResponse } from "../types/demoEntryAnalysis";
import {
  clearDemoClaimInFlight,
  getDemoClaimInFlight,
  markDemoClaimStarted,
  setDemoClaimInFlight,
  unmarkDemoClaimStarted,
} from "./demoEntryAnalysisGates";
import { readDemoSession, writeDemoSession } from "./demoEntryAnalysisSession";

export { hasDemoClaimStarted, resetDemoClaimGate } from "./demoEntryAnalysisGates";

/** True if this job must not call POST /claim again (successful claim only). */
export function shouldSkipDemoClaim(jobId: string): boolean {
  const session = readDemoSession();
  if (!session || session.jobId !== jobId) return false;
  return Boolean(session.claimed);
}

/**
 * POST /claim at most once per jobId (tab gate + session claimAttempted).
 * Call only when Clerk is signed in, poll cookie OK, and status is preparing|prep_ready.
 */
export function claimDemoJobOnce(jobId: string): Promise<DemoJobPollResponse> {
  if (shouldSkipDemoClaim(jobId)) {
    return Promise.reject(new Error("Demo claim already attempted for this job."));
  }

  const existing = getDemoClaimInFlight<DemoJobPollResponse>(jobId);
  if (existing) return existing;

  if (!markDemoClaimStarted(jobId)) {
    return Promise.reject(new Error("Demo claim already started for this job."));
  }

  // Persist before network so reload cannot fire a second claim.
  writeDemoSession({ claimAttempted: true });

  const promise = (async () => {
    try {
      const res = await claimDemoJob(jobId);
      writeDemoSession({ claimed: true, claimAttempted: true, handoffCode: undefined });
      return res;
    } catch (err) {
      writeDemoSession({ claimAttempted: false });
      unmarkDemoClaimStarted(jobId);
      throw err;
    } finally {
      clearDemoClaimInFlight(jobId);
    }
  })();

  setDemoClaimInFlight(jobId, promise);
  return promise;
}

/**
 * After redeem restores the poll cookie, permit one more /claim only if the first
 * attempt never reached the network successfully (claimAttempted cleared by caller).
 */
export function allowDemoClaimRetryAfterCookieFix(jobId: string): void {
  if (readDemoSession()?.claimed) return;
  unmarkDemoClaimStarted(jobId);
  clearDemoClaimInFlight(jobId);
  // Keep claimAttempted — cookie retry is the only allowed second POST, gated by unmark + explicit clear:
  writeDemoSession({ claimAttempted: false });
}
